import {chatApi,type ChatMessage,type ChatMeta,type ChatSessionRow,type ChatStreamEvent} from '@/data/api/chat'
/**
 * Session Store · Pinia
 * ------------------------------------------------------------
 * 原型对应：WorkShell.conversations / recordTurn / sendQuestion / getBlocks / openChat
 *
 * 职责：
 * - sessions 列表 + currentId；新建会话 / 追加消息 / 会话标题（首条提问前 18 字）
 * - CHATS（sceneRepo.chats()，按组织分组）作为「近期会话」预置数据，切组织整组替换
 * - 回答键解析（原型 getBlocks）：ANSWERS 直答 + dom:/ep:/nd:/rp: 视角前缀路由
 *
 * 会话状态流转：work →（发起话题）→ topic →（邀请协作）→ group
 * 状态机（machine.ts）在 M3 落地；M1 会话一律 'work'。
 */
import type {CloudFile} from '@/data/api/cloud-files'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { sceneRepo, orgRepo, answerRepo } from '@/data/api/shell-repository'
import { emit, on } from '@/shared/events'

/* ---------- 结构化回答块（与 mock/scenes.js 的 ANSWERS 块形状对齐的 DTO） ---------- */
export interface KpiItem { l: string; v: string; u?: string; d?: string; tone?: string }
export interface ArchApp { code: string; name: string; role?: string; lv?: string }
export interface FlowStep { t: string; c?: string; d?: string; s?: string }
export interface BarItem { lb: string; v: number; tone?: string; code?: string; name?: string }
export interface RoomMember { n: string; r?: string; col?: string; why?: string; need?: string }
export interface FileRow { n: string; m: string; ic?: string; col?: string; tags?: Array<[string, string]> }
export interface ItemCard {
  nm: string
  badge?: Array<string | [string, string]>
  right?: string
  code?: string
  go?: string
  kv?: Array<[string, any]>
  why?: string
  acts?: Array<string | [string, string]>
}
export type BadgeTone = string
export type ActItem = string | [string, string]
export type AnswerBlock =
  | { t: 'say'; html: string }
  | { t: 'note'; html: string }
  | { t: 'kpis'; items: KpiItem[] }
  | { t: 'items'; items: ItemCard[] }
  | { t: 'table'; title?: string; cols: string[]; rows: any[][] }
  | { t: 'bars'; title?: string; items: BarItem[]; target?: number }
  | { t: 'arch'; title?: string; items: ArchApp[]; note?: string }
  | { t: 'flow'; title?: string; steps: FlowStep[] }
  | { t: 'room'; title: string; tag?: string; done?: boolean; name?: string; pattern?: string;
      members?: RoomMember[]; msrc?: string; rules?: Array<[string, any]>; ctx?: string; acts?: ActItem[] }
  | { t: 'files'; title?: string; items: FileRow[] }
  | { t: 'acts'; items: ActItem[] }

/* ---------- 会话与消息 ---------- */
export type SessionState = 'work' | 'topic' | 'group'
export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  ts: number
  blocks?: AnswerBlock[]
  pending?: boolean
  chatJobId?:string
  chatStatus?:ChatMessage['status']
  sourceId?:string
  createdAt?:string
  meta?: ChatMeta | null
  model?: string | null
  notice?: string
  attachments?: CloudFile[]
  answerKey?: string
  feedback?: 'up' | 'down' | null
  branchIndex?: number
  branchCount?: number
  prevBranchId?: string | null
  nextBranchId?: string | null
  /** 本条回答实际调用的工具名列表（后端从 chat.tool_call 聚合），用于"调用了工具：xxx"提示 */
  toolNames?: string[]
}
export interface SessionTurn { q: string; a: string }
export interface Session {
  id: string
  title: string
  state: SessionState
  scene: string
  messages: Message[]
  updatedAt: number
  /** 来自 CHATS 的近期会话预置（turns 未物化前 messages 为空） */
  preset?: boolean
  persisted?: boolean
  serverTitle?: boolean
  headId?: string | null
  hasOlder?: boolean
  historyDone?: boolean
  turns?: SessionTurn[]
  /** 置顶：列表按置顶优先排序 */
  pinned?: boolean
  /** 会话健康：系统阻断 / 需再处理；空表示正常 */
  health?: 'ok' | 'blocked' | 'attention'
}

/** 本次操作与能力（回答服务显式上报给右栏的调用面摘要） */
export interface LiveOps { q: string; codes: string[]; blockCount: number }

/** AI 操作追踪（右栏透明面板数据） */
export type AiStepStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped'
export interface AiOpStep {
  id: string
  title: string        // 操作标题，如"解析意图"、"匹配应用架构"、"取数并组装回答"
  detail?: string      // 操作的具体内容（如匹配到哪几个应用）
  status: AiStepStatus
  ts: number
  duration?: number    // 完成耗时（ms）
  tone?: 'info' | 'accent' | 'warn' | 'teal'
}
export interface AiThinkingItem {
  id: string
  tag: string          // 标签：意图 / 数据来源 / 授权 / 判断 / 兜底
  text: string         // 思考内容
  ts: number
}
export interface AiContext {
  /** 业务主线（如 "异常闭环" / "采购到货" / "预算执行"） */
  businessLine: string
  /** 当前业务节点（如 "定级与分派" / "计量质量与验收结论"） */
  currentNode: string
  /** 当前对象编号（如异常编号 EXC-2026-10-0087） */
  objectNo: string
  /** 当前对象所属业务节点 */
  bizNode: string
  /** 涉及单据（多个，斜杠分隔） */
  relatedDocs: string
  /** 责任组织 */
  org: string
  /** 会计期间 */
  period: string
  /** 异常条件/范围说明 */
  scopeNote: string
  /** 权限描述 */
  authority: string
}
export interface AiTrace {
  context: AiContext | null
  thinking: AiThinkingItem[]
  planned: AiOpStep[]   // 即将做的（pending + running）
  history: AiOpStep[]   // 已经做过的（done + failed + skipped）
}

let seq = 0
const uid = (p: string) => `${p}-${Date.now().toString(36)}-${(++seq).toString(36)}`

export const useSessionStore = defineStore('session', () => {
  const sessions = ref<Session[]>([])
  const currentId = ref<string | null>(null)
  /** 发送/恢复会话期间的忙碌标志（原型 busy） */
  const busy = ref(false)
  const opening = ref(false)
  const sessionsLoading = ref(false)
  const sessionsError = ref('')
  const sessionHasMore = ref(false)
  const sessionQuery = ref('')
  const matchedIds = ref<string[] | null>(null)
  const sessionOffset = ref(0)
  /**
   * 本次操作与能力（右栏 live）：回答完成后由会话服务（本 store，M2+ 为 AI
   * 回答服务）显式上报，替换 UI 从 blocks 反向提取的临时方案。
   */
  const live = ref<LiveOps | null>(null)

  /** AI 操作追踪（右栏透明面板）：单条会话内全程累积 */
  const aiTrace = ref<AiTrace>({ context: null, thinking: [], planned: [], history: [] })

  /* ---------- trace 辅助方法 ---------- */
  function traceReset() {
    aiTrace.value = { context: null, thinking: [], planned: [], history: [] }
  }
  function setContext(ctx: Partial<AiContext>) {
    aiTrace.value.context = {
      businessLine: '', currentNode: '', objectNo: '', bizNode: '',
      relatedDocs: '', org: '', period: '', scopeNote: '', authority: '',
      ...(aiTrace.value.context ?? {}), ...ctx
    }
  }
  function addThinking(tag: string, text: string) {
    aiTrace.value.thinking.unshift({ id: uid('think'), tag, text, ts: Date.now() })
    if (aiTrace.value.thinking.length > 20) aiTrace.value.thinking.pop()
  }
  function planStep(title: string, detail?: string, tone?: AiOpStep['tone']): AiOpStep {
    const step: AiOpStep = { id: uid('step'), title, detail, status: 'pending', ts: Date.now(), tone }
    aiTrace.value.planned.push(step)
    return step
  }
  function runStep(id: string) {
    const s = aiTrace.value.planned.find(x => x.id === id)
    if (s) { s.status = 'running'; s.ts = Date.now() }
  }
  function finishStep(id: string, status: AiStepStatus = 'done', detailOverride?: string) {
    const idx = aiTrace.value.planned.findIndex(x => x.id === id)
    if (idx < 0) return
    const s = aiTrace.value.planned[idx]
    s.status = status
    if (detailOverride) s.detail = detailOverride
    s.duration = Date.now() - s.ts
    aiTrace.value.planned.splice(idx, 1)
    aiTrace.value.history.unshift(s)
    if (aiTrace.value.history.length > 30) aiTrace.value.history.pop()
  }

  /** 显式上报：计算当前会话最后一轮的调用面（被调用应用/构建块编码 + 结构化块数） */
  function reportLive(s: Session | null) {
    if (!s) { live.value = null; return }
    const msgs = s.messages
    const lastUser = [...msgs].reverse().find(m => m.role === 'user')
    if (!lastUser) { live.value = null; return }
    const ans = [...msgs].reverse().find(m => m.role === 'assistant' && m.blocks?.length && !m.pending)
    const codes: string[] = []
    for (const b of ans?.blocks || []) {
      if (b.t === 'arch' || b.t === 'items') b.items.forEach(i => { if (i.code && !codes.includes(i.code)) codes.push(i.code) })
    }
    live.value = { q: lastUser.content, codes: codes.slice(0, 8), blockCount: ans ? (ans.blocks?.length ?? 0) : -1 }
  }

  const current = computed(() => sessions.value.find(s => s.id === currentId.value) ?? null)
  const count = computed(() => sessions.value.length)
  const visibleSessions = computed(() => {
    /* 空的新会话（尚未发送、未落库）不进入清单，首次发送后才出现 */
    const real = sessions.value.filter(s => !s.preset && (s.persisted || s.messages.length > 0))
    const q = sessionQuery.value.trim().toLowerCase()
    if (!q || !matchedIds.value) return real
    const ids = new Set(matchedIds.value)
    return real.filter(s => ids.has(s.id) || (!s.persisted && s.title.toLowerCase().includes(q)))
  })

  /* ---------- 回答键解析（原型 getBlocks；按需加载架构集合，见 answerRepo） ---------- */
  async function resolveBlocks(key: string): Promise<AnswerBlock[] | null> {
    if (!key) return null
    const v = await answerRepo.answer(key)
    if (typeof v === 'function') return v() as AnswerBlock[]
    if (v) return v as AnswerBlock[]
    /* 视角前缀：从任何列表下钻到具体架构元素（dom:/ep:/nd:/rp:） */
    if (key.startsWith('dom:')) return (await answerRepo.domAnswer(key.slice(4))) as AnswerBlock[]
    if (key.startsWith('ep:')) return (await answerRepo.epAnswer(key.slice(3))) as AnswerBlock[]
    if (key.startsWith('nd:')) return (await answerRepo.ndAnswer(key.slice(3))) as AnswerBlock[]
    if (key.startsWith('rp:')) return (await answerRepo.rpAnswer(key.slice(3))) as AnswerBlock[]
    return null
  }

  /** 自由文本兜底回答（原型 sendQuestion 无预置键时的演示路径） */
  function fallbackBlocks(): AnswerBlock[] {
    return [
      { t: 'say', html: '我是原型演示版。真实运行时会这样处理：先做<b>意图解析</b>，再在应用架构里匹配应用，然后按授权范围取数——你的自由文本同样能跑通，这里只是没预置演示数据。' },
      { t: 'arch', title: '意图解析 · 拟调用路径', items: [
        { code: 'AA_DigitalPlatform_AI_Assistant', name: '智能助理管理', role: '意图解析', lv: 'L3' },
        { code: 'AA_DigitalPlatform_IdentityAndAccess_Access', name: '权限管理', role: '授权范围校验', lv: 'L3' },
        { code: 'AA_DigitalPlatform_Exception_Ledger', name: '异常台账管理', role: '数据入口', lv: 'L3' }
      ], note: '点左侧「应用架构」可以看到全部应用，任意一个都能直接发起调用。' }
    ]
  }

  /* ---------- 近期会话预置（按当前组织整组替换；真实组织返回空数组） ---------- */
  async function seedForOrg(orgId?: string) {
    const id = orgId || orgRepo.id()
    const list = await sceneRepo.chats().catch(() => [])
    /* 只保留真实会话（后端会话 / 材料会话 / 新建会话），演示预置整组替换，避免延后加载时误清真实数据 */
    const personal = sessions.value.filter(s => !s.preset)
    sessions.value = [...personal, ...list.map((c, i) => ({
      id: `chat-${id}-${i}`,
      title: String(c.t ?? '未命名会话'),
      state: 'work' as SessionState,
      scene: String(c.sc ?? 'workbench'),
      messages: [],
      turns: (Array.isArray(c.turns) ? c.turns : []).map((t: any) => ({ q: String(t.q ?? ''), a: String(t.a ?? '') })),
      preset: true,
      updatedAt: Date.now() - i * 60000
    }))]
    if (currentId.value && !sessions.value.some(s => s.id === currentId.value)) {
      currentId.value = null
      live.value = null
    }
  }

  /* ---------- 会话生命周期 ---------- */
  function newSession(scene = 'workbench'): Session {
    // 防重复创建：如果已经有一个空的新会话（默认标题、无消息、work 态），直接切换过去
    const existingEmpty = sessions.value.find(
      s => !s.preset && s.title === '新会话' && s.messages.length === 0 && s.state === 'work'
    )
    if (existingEmpty) {
      currentId.value = existingEmpty.id
      live.value = null
      return existingEmpty
    }
    const s: Session = {
      id: crypto.randomUUID(), title: '新会话', state: 'work', scene,
      messages: [], updatedAt: Date.now()
    }
    sessions.value.unshift(s)
    currentId.value = s.id
    live.value = null
    return s
  }

  /** 打开近期会话：物化预置问答（解析回答键 → 结构化块），一次渲染全部 */
  async function openSession(id: string) {
    if (busy.value) return
    const s = sessions.value.find(x => x.id === id)
    if (!s) return
    currentId.value = id
    if (!s.preset && s.persisted && !s.messages.some(m => m.chatJobId)) {
      opening.value = true
      busy.value = true
      try {
        const page = await applyThread(id)
        if (s.hasOlder === undefined) s.hasOlder = page.messages.length === 40
      } catch (e) {
        sessionsError.value = e instanceof Error ? e.message : '无法读取消息'
      } finally { busy.value = false; opening.value = false }
    }
    /* 切换会话即上报该会话当前调用面（已物化则直接取最后一轮） */
    if (!s.preset || s.messages.length) { reportLive(s); return }
    busy.value = true
    try {
      for (const t of s.turns ?? []) {
        s.messages.push({ id: uid('m'), role: 'user', content: t.q, ts: Date.now() })
        let blocks: AnswerBlock[] | null = null
        try { blocks = await resolveBlocks(t.a) } catch { blocks = null }
        s.messages.push({
          id: uid('m'), role: 'assistant', content: '', ts: Date.now(),
          blocks: blocks && blocks.length ? blocks : fallbackBlocks(), answerKey: t.a
        })
      }
      s.updatedAt = Date.now()
    } finally { busy.value = false }
    reportLive(s) /* 预置问答物化完成 → 上报最后一轮调用面 */
  }

  function appendMessage(m: Omit<Message, 'id' | 'ts'>, s: Session | null = current.value): Message {
    const msg: Message = { ...m, id: uid('m'), ts: Date.now() }
    if (s) { s.messages.push(msg); s.updatedAt = Date.now() }
    return msg
  }

  /**
   * 发送一问（原型 sendQuestion）：用户消息 → 思考占位 → 结构化回答。
   * busy 期间拒绝并发；标题取首条提问前 18 字。
   * 全程同时更新 aiTrace（右栏透明面板数据）。
   */
  async function ask(question: string, answerKey?: string | null, scene?: string) {
    const text = String(question ?? '').trim()
    if (!text || busy.value) return
    busy.value = true
    try {
      let s = current.value
      if (!s) s = newSession(scene ?? 'workbench')
      if (scene) s.scene = scene
      appendMessage({ role: 'user', content: text }, s)
      /* 会话标题 = 首条提问前 18 字 */
      if (s.messages.filter(m => m.role === 'user').length === 1) s.title = text.slice(0, 18) || '新会话'

      /* 演示回答只留在对话区。右栏背景和下一步只认已落库的数据，这里不再写入示例编号。 */
      const pend = appendMessage({ role: 'assistant', content: '', pending: true }, s)
      let blocks: AnswerBlock[] | null = null
      if (answerKey) {
        try { blocks = await resolveBlocks(answerKey) } catch { blocks = null }
      }
      if (!blocks?.length) blocks = fallbackBlocks()
      pend.blocks = blocks
      pend.pending = false
      pend.answerKey = answerKey ?? undefined
      s.updatedAt = Date.now()
      reportLive(s)
    } finally { busy.value = false }
  }

  const statusText: Record<ChatMessage['status'], string> = {
    queued: '', running: '', ready: '',
    failed: '回答未完成。可以重试，系统不会自动重发。',
    cancelled: '已停止回答。',
    interrupted: '回答已中断。可以重试，系统不会自动重发。',
  }
  function upsertSessionRow(row: ChatSessionRow) {
    const at = new Date(row.updated_at).getTime()
    const title = (row.title || '').trim() || '新会话'
    const pinned = !!row.pinned_at
    const health = row.health || undefined
    let session = sessions.value.find(s => s.id === row.id)
    if (!session) {
      session = { id: row.id, title, state: 'work', scene: 'workbench', messages: [], updatedAt: at, persisted: true, serverTitle: true, pinned, health }
      sessions.value.unshift(session)
    } else if (!session.preset) {
      session.title = title
      session.updatedAt = Math.max(session.updatedAt, at)
      session.persisted = true
      session.serverTitle = true
      session.pinned = pinned
      if (health) session.health = health
    }
    return session
  }
  function mergeChat(rows: ChatMessage[]) {
    for (const row of [...rows].reverse()) {
      let session = sessions.value.find(s => s.id === row.session_id)
      const at = new Date(row.created_at).getTime()
      if (!session) {
        session = { id: row.session_id, title: row.user_text.trim().replace(/\s+/g, ' ').slice(0, 40) || '新会话', state: 'work', scene: 'workbench', messages: [], updatedAt: at, persisted: true }
        sessions.value.unshift(session)
      }
      const streaming = row.status === 'queued' || row.status === 'running'
      const notice = streaming || row.status === 'ready' ? '' : statusText[row.status]
      const fileIds = new Set((row.meta?.files || []).map(file => file.id))
      const orphans = fileIds.size ? session.messages.filter(m => !m.sourceId && m.role === 'user' && m.attachments?.some(file => fileIds.has(file.file_id))) : []
      const carried = orphans.flatMap(m => m.attachments || []).filter(file => fileIds.has(file.file_id))
      if (orphans.length) session.messages = session.messages.filter(m => !orphans.includes(m))
      const pair: Message[] = [
        { id: row.id + '-user', role: 'user', content: row.user_text, ts: at, sourceId: row.id, createdAt: row.created_at, meta: row.meta || null, attachments: row.attachments?.length ? row.attachments : carried.length ? carried : undefined, branchIndex: row.branch_index, branchCount: row.branch_count, prevBranchId: row.prev_branch_id, nextBranchId: row.next_branch_id },
        { id: row.id + '-assistant', role: 'assistant', content: row.assistant_text || '', ts: at + 1, sourceId: row.id, createdAt: row.created_at, chatJobId: row.id, chatStatus: row.status, model: row.model || null, pending: streaming && !row.assistant_text, notice, feedback: row.feedback || null, branchIndex: row.branch_index, branchCount: row.branch_count, prevBranchId: row.prev_branch_id, nextBranchId: row.next_branch_id, toolNames: Array.isArray(row.tool_names) ? row.tool_names : [] },
      ]
      for (const message of pair) {
        const index = session.messages.findIndex(m => m.id === message.id)
        if (index < 0) session.messages.push(message)
        else session.messages[index] = { ...message, attachments: message.attachments?.length ? message.attachments : session.messages[index].attachments }
      }
      session.messages.sort((a, b) => a.ts - b.ts)
      session.updatedAt = Math.max(session.updatedAt, at)
      session.persisted = true
      if (streaming) follow(row.id)
    }
  }
  function keepPath(session: Session, pathIds: string[] | null) {
    if (!pathIds) return
    const ids = new Set(pathIds)
    session.messages = session.messages.filter(message => !message.sourceId || ids.has(message.sourceId))
  }
  async function applyThread(sessionId: string, before?: string, beforeId?: string) {
    const page = await chatApi.thread(sessionId, before, beforeId)
    const session = sessions.value.find(item => item.id === sessionId)
    if (session) { keepPath(session, page.pathIds); session.headId = page.headId }
    mergeChat(page.messages)
    return page
  }
  const streamOn = ref(false)
  let streamAbort: AbortController | undefined
  function follow(id: string) {
    if (streamAbort && streamOn.value) return
    streamAbort?.abort()
    const controller = new AbortController()
    streamAbort = controller
    streamOn.value = true
    void chatApi.watch(id, event => applyStream(event), controller.signal).catch(() => {}).finally(() => {
      if (streamAbort === controller) { streamOn.value = false; streamAbort = undefined }
    })
  }
  function applyStream(event: ChatStreamEvent) {
    const id = event.data.id
    if (!id) return
    const status = event.data.status
    const text = event.data.text || ''
    const streaming = status === 'queued' || status === 'running'
    const notice = !status || streaming || status === 'ready' ? '' : statusText[status]
    /* 会话状态色跟随最近一条消息：失败=系统阻断，中断/取消=需再处理，完成=正常 */
    const health = status === 'failed' ? 'blocked' : status === 'interrupted' || status === 'cancelled' ? 'attention' : status === 'ready' ? 'ok' : ''
    let hitSessionId = ''
    for (const session of sessions.value) {
      const message = session.messages.find(item => item.chatJobId === id)
      if (!message) continue
      hitSessionId = session.id
      message.content = text
      if (status) message.chatStatus = status
      message.pending = streaming && !text
      message.notice = notice
      if (health) session.health = health
    }
    /* 广播回答活动（阶段+状态），供企管助手小人全站念叨；流断开不广播，由活动模块超时兜底清场 */
    if (hitSessionId) emit('chat:activity', { messageId: id, sessionId: hitSessionId, status: status || '', phase: event.data.phase ?? null })
    if (event.event === 'done' || event.event === 'error') streamAbort?.abort()
  }
  /** 列表顺序：置顶优先，其次按更新时间倒序 */
  function orderSessions() {
    sessions.value.sort((a, b) => (Number(!!b.pinned) - Number(!!a.pinned)) || b.updatedAt - a.updatedAt)
  }
  async function loadSessions(reset = true) {
    sessionsLoading.value = true
    sessionsError.value = ''
    try {
      const offset = reset ? 0 : sessionOffset.value
      const rows = await chatApi.sessions(sessionQuery.value.trim(), offset)
      for (const row of rows) upsertSessionRow(row)
      sessionOffset.value = offset + rows.length
      sessionHasMore.value = rows.length === 40
      if (sessionQuery.value.trim()) matchedIds.value = reset ? rows.map(row => row.id) : [...(matchedIds.value || []), ...rows.map(row => row.id)]
      else if (reset) matchedIds.value = null
      orderSessions()
      return rows.length
    } catch (e) {
      sessionsError.value = e instanceof Error ? e.message : '无法读取会话'
      throw e
    } finally { sessionsLoading.value = false }
  }
  async function searchSessions(q: string) {
    sessionQuery.value = q
    return loadSessions(true)
  }
  async function refreshChat() {
    await loadSessions(true)
    const id = currentId.value
    const session = id ? sessions.value.find(s => s.id === id) : undefined
    if (!session?.persisted) return 0
    const page = await applyThread(id!)
    if (session.hasOlder === undefined) session.hasOlder = page.messages.length === 40
    return page.messages.length
  }
  async function loadOlder(id: string) {
    const session = sessions.value.find(s => s.id === id)
    if (!session?.hasOlder || session.historyDone) return 0
    const oldest = session.messages.find(m => m.createdAt && m.sourceId)
    if (!oldest?.createdAt || !oldest.sourceId) return 0
    const page = await applyThread(id, oldest.createdAt, oldest.sourceId)
    if (page.messages.length < 40) { session.hasOlder = false; session.historyDone = true }
    return page.messages.length
  }
  const pendingSessionIds = new Map<string, string>()
  async function askReal(text: string, requestKey: string, meta?: ChatMeta) {
    if (busy.value) return
    busy.value = true
    try {
      const local = current.value
      const sessionId = pendingSessionIds.get(requestKey) || (local?.id.match(/^[0-9a-f-]{36}$/) ? local.id : crypto.randomUUID())
      pendingSessionIds.set(requestKey, sessionId)
      const title = local && local.id === sessionId && !local.serverTitle && local.title !== '新会话' ? local.title : undefined
      const row = await chatApi.send(sessionId, text, requestKey, meta, title)
      mergeChat([row])
      const session = sessions.value.find(s => s.id === sessionId)
      if (session) { session.persisted = true; session.headId = row.id }
      currentId.value = sessionId
      try { await loadSessions(true) } catch { /* 列表失败不影响已经发出的消息 */ }
      emit('assistant:refresh')
    } finally { busy.value = false }
  }
  async function replaceWithHead(sessionId: string) {
    const session = sessions.value.find(item => item.id === sessionId)
    if (!session) return
    session.persisted = true
    session.hasOlder = undefined
    session.historyDone = false
    await applyThread(sessionId)
  }
  async function retryReal(id: string) {
    if (busy.value) throw Error('请等待当前回答')
    busy.value = true
    try { await replaceWithHead((await chatApi.retry(id)).session_id); emit('assistant:refresh') } finally { busy.value = false }
  }
  async function editReal(id: string, text: string, meta?: ChatMeta) {
    if (busy.value) throw Error('请等待当前回答')
    busy.value = true
    try { await replaceWithHead((await chatApi.edit(id, text, meta)).session_id); emit('assistant:refresh') } finally { busy.value = false }
  }
  async function renameSession(id: string, title: string) {
    const clean = title.trim()
    if (!clean || clean.length > 40) throw Error('标题需要 1 到 40 个字')
    const session = sessions.value.find(s => s.id === id)
    if (!session || session.preset) return
    if (!session.persisted) { session.title = clean; return }
    upsertSessionRow(await chatApi.rename(id, clean))
  }
  async function deleteSession(id: string) {
    const session = sessions.value.find(s => s.id === id)
    if (session?.persisted) await chatApi.remove(id)
    sessions.value = sessions.value.filter(s => s.id !== id)
    if (matchedIds.value) matchedIds.value = matchedIds.value.filter(item => item !== id)
    if (currentId.value === id) { currentId.value = null; live.value = null }
    emit('assistant:refresh')
  }
  /** 置顶 / 取消置顶：只改本人会话标记，服务端返回最新行 */
  async function togglePin(id: string, pinned: boolean) {
    const session = sessions.value.find(s => s.id === id)
    if (!session?.persisted) throw Error('会话尚未保存，暂不能置顶')
    upsertSessionRow(await chatApi.pin(id, pinned))
    orderSessions()
  }
  /** 归档：服务端标记归档时间，随后从当前列表移除 */
  async function archiveSession(id: string) {
    const session = sessions.value.find(s => s.id === id)
    if (!session?.persisted) throw Error('会话尚未保存，暂不能归档')
    await chatApi.archive(id, true)
    sessions.value = sessions.value.filter(s => s.id !== id)
    if (matchedIds.value) matchedIds.value = matchedIds.value.filter(item => item !== id)
    if (currentId.value === id) { currentId.value = null; live.value = null }
    emit('assistant:refresh')
  }
  async function cancelChat(id: string) { await chatApi.cancel(id); await refreshChat(); emit('assistant:refresh') }
  async function activateBranch(messageId: string) {
    const session = current.value
    if (!session?.persisted || busy.value) throw Error('请等待当前回答')
    if (session.headId === undefined) throw Error('请刷新会话后再切换分支')
    await chatApi.activate(session.id, messageId, session.headId)
    session.hasOlder = undefined
    session.historyDone = false
    await applyThread(session.id)
  }
  async function setFeedback(id: string, vote: 'up' | 'down' | null) {
    const saved = await chatApi.feedback(id, vote)
    for (const session of sessions.value) for (const message of session.messages) if (message.sourceId === id && message.role === 'assistant') message.feedback = saved.vote
    return saved
  }

  /* 组织切换 → 近期会话整组替换、回到场景区（与原型「切组织整组替换」对齐）
     演示预置依赖 mock/scenes（含大型架构数据），延后加载：先让真实会话列表出来，
     预置就绪后整组替换演示会话，不阻塞首屏。 */
  let seedTimer: ReturnType<typeof setTimeout> | undefined
  on('org:changed', () => {
    currentId.value = null
    live.value = null
    if (seedTimer) clearTimeout(seedTimer)
    seedTimer = setTimeout(() => {
      seedTimer = undefined
      void seedForOrg().catch(() => { /* 预置不可用不影响真实会话 */ })
    }, 1200)
  })

  return {
    sessions, currentId, current, count, busy, opening, live, streamOn,
    sessionsLoading, sessionsError, sessionHasMore, visibleSessions,
    aiTrace,
    resolveBlocks, fallbackBlocks, seedForOrg,
    newSession, openSession, appendMessage, ask, refreshChat, loadSessions, searchSessions, loadOlder,
    askReal, retryReal, editReal, renameSession, deleteSession, togglePin, archiveSession, cancelChat, activateBranch, setFeedback,
    traceReset, setContext, addThinking, planStep, runStep, finishStep
  }
})
